import PropTypes from 'prop-types';
import React from 'react';
import { FormattedMessage, useIntl } from 'react-intl';
import { getPasswordValidationDetails } from '@htmniseko/htm-validators/utils/password';
import { Icon } from 'components/HeroSemantic';

const ruleColor = (satisfied, dim) => {
  if (satisfied) {return 'var(--owner-success)';}
  if (dim) {return 'var(--owner-text-muted)';}
  return 'var(--owner-text)';
};

const Rule = ({ satisfied, dim, label, indent }) => (
  <li
    className="flex items-start gap-2"
    style={{
      color: ruleColor(satisfied, dim),
      fontSize: '12px',
      lineHeight: '18px',
      paddingLeft: indent ? '16px' : 0,
    }}
  >
    <Icon
      name={satisfied ? 'check' : 'circle'}
      style={{ width: '14px', height: '14px', marginTop: '2px', flexShrink: 0 }}
    />
    <span>{label}</span>
  </li>
);

Rule.propTypes = {
  satisfied: PropTypes.bool,
  dim: PropTypes.bool,
  label: PropTypes.node.isRequired,
  indent: PropTypes.bool,
};

const PasswordValidationHelper = ({ password }) => {
  const { formatMessage } = useIntl();
  const isPristine = !password;
  const {
    hasMinLength,
    hasLowerCase,
    hasUpperCase,
    hasNumber,
    hasSymbol,
    hasMinCombination,
  } = getPasswordValidationDetails(password);

  return (
    <div
      role="status"
      style={{
        background: 'var(--owner-surface-muted)',
        border: '1px solid var(--owner-border)',
        borderRadius: '12px',
        padding: '14px 16px',
      }}
    >
      <div
        style={{
          color: 'var(--owner-text-muted)',
          fontSize: '11px',
          fontWeight: 600,
          letterSpacing: '0.04em',
          lineHeight: '16px',
          marginBottom: '10px',
          textTransform: 'uppercase',
        }}
      >
        <FormattedMessage id="page.user.changePassword.passwordRequirements" />
      </div>
      <ul style={{ display: 'flex', flexDirection: 'column', gap: '6px', margin: 0, listStyle: 'none', padding: 0 }}>
        <Rule
          satisfied={hasMinLength}
          dim={isPristine}
          label={formatMessage({ id: 'page.user.changePassword.has8Chars' })}
        />
        <Rule
          satisfied={hasMinCombination}
          dim={isPristine}
          label={formatMessage({ id: 'page.user.changePassword.atleast2Combinations' })}
        />
        <Rule
          satisfied={hasLowerCase}
          dim={isPristine || !hasMinCombination}
          label={formatMessage({ id: 'page.user.changePassword.lowerCase' })}
          indent
        />
        <Rule
          satisfied={hasUpperCase}
          dim={isPristine || !hasMinCombination}
          label={formatMessage({ id: 'page.user.changePassword.upperCase' })}
          indent
        />
        <Rule
          satisfied={hasNumber}
          dim={isPristine || !hasMinCombination}
          label={formatMessage({ id: 'page.user.changePassword.numbers' })}
          indent
        />
        <Rule
          satisfied={hasSymbol}
          dim={isPristine || !hasMinCombination}
          label={formatMessage({ id: 'page.user.changePassword.symbols' })}
          indent
        />
      </ul>
    </div>
  );
};

PasswordValidationHelper.propTypes = {
  password: PropTypes.string,
};

PasswordValidationHelper.defaultProps = {
  password: '',
};

export default PasswordValidationHelper;
